/**
 * IVM Color Palettes + 256-entry LUT Baking
 *
 * Each palette is defined as a list of hex color stops.
 * bakeLUT() pre-computes 256 CSS rgb() strings for O(1) lookup.
 * Presets map a state value [0,1] to a LUT index.
 */

export interface RGBColor {
  r: number;
  g: number;
  b: number;
}

export type ColorLUT = string[]; // 256 entries, CSS color strings

function hexToRGB(hex: string): RGBColor {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function lerpRGB(a: RGBColor, b: RGBColor, t: number): RGBColor {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

function rgbToCSS(c: RGBColor, alpha?: number): string {
  if (alpha !== undefined && alpha < 1) {
    return `rgba(${c.r},${c.g},${c.b},${alpha.toFixed(2)})`;
  }
  return `rgb(${c.r},${c.g},${c.b})`;
}

/**
 * Bake a 256-entry LUT from color stops.
 * Stops are evenly distributed across [0,255].
 */
export function bakeLUT(stops: string[]): ColorLUT {
  const lut: string[] = new Array(256);
  const rgbStops = stops.map(hexToRGB);
  const n = rgbStops.length - 1;

  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    const segment = t * n;
    const idx = Math.min(Math.floor(segment), n - 1);
    const frac = segment - idx;
    const color = lerpRGB(rgbStops[idx], rgbStops[idx + 1], frac);
    lut[i] = rgbToCSS(color);
  }
  return lut;
}

/** Map a [0,1] value to a LUT index */
export function lutLookup(lut: ColorLUT, value: number): string {
  const idx = Math.max(0, Math.min(255, Math.round(value * 255)));
  return lut[idx];
}

// === Palette Definitions ===

const PALETTE_STOPS = {
  // Dark amber to gold to white — sacred architecture
  cathedral: ['#0a0a14', '#1a0f08', '#4a2810', '#b8741a', '#d4a030', '#f0d060', '#fffbe8'],
  // Navy to red to white-gold — heat map
  thermal: ['#0a1628', '#102040', '#2040a0', '#a02020', '#e06030', '#f0c050', '#fffbe0'],
  // Blue to neutral to gold — bipolar charge
  polarity: ['#1040c0', '#3060d0', '#6080c0', '#808080', '#c0a060', '#d0b030', '#e0c010'],
  // Binary: transparent(off) / blue(on)
  conway: ['#0a0f1a', '#1BB1E7'],
  // Dark to blue-green to white — organic growth
  mycelium: ['#060810', '#0a1820', '#104040', '#20a080', '#60d0a0', '#a0f0d0', '#e0fff0'],
  // §01 Network brand greens — invisible-on-bg through filament to bright accent.
  // Anchored to the imported tokens (#369369 primary, #339966 secondary).
  network: ['#0a1410', '#0d2018', '#143020', '#1f4e34', '#369369', '#5cc090', '#a8e6c8'],
  // White to dark — monochrome
  mono: ['#e8e8f0', '#b0b0c0', '#707088', '#404060', '#202040', '#101028', '#0a0a18'],
  // Charge-driven: dark → purple → white flash
  storm: ['#0a0a18', '#200830', '#401060', '#8020b0', '#c040e0', '#e080ff', '#ffffff'],
  // Per-lane: cyan / magenta / gold
  riverCyan: ['#0a1418', '#104050', '#20a0c0', '#40d0e8', '#80f0ff'],
  riverMagenta: ['#180a18', '#401050', '#a020c0', '#d040e8', '#ff80ff'],
  riverGold: ['#18140a', '#504010', '#c0a020', '#e8d040', '#fff080'],
  // Clan colors — 6 distinct hues
  clan0: ['#0a0a18', '#1040c0', '#3080e0'],  // blue
  clan1: ['#0a180a', '#10a040', '#40d060'],   // green
  clan2: ['#180a0a', '#c01020', '#e04040'],   // red
  clan3: ['#18180a', '#c0a010', '#e0d040'],   // gold
  clan4: ['#180a18', '#a010c0', '#d040e0'],   // purple
  clan5: ['#0a1818', '#10a0a0', '#40d0d0'],   // cyan
  // Fibonacci — subtle warm
  fibonacci: ['#0a0f1a', '#1a1520', '#3a2530', '#704840', '#b08060', '#e0c0a0'],
  // Phase transition: cold blue → warm orange
  phase: ['#0a1040', '#1030a0', '#3060d0', '#808080', '#d09030', '#e0a010', '#f0c000'],
  // Wound healing: red damage → green heal
  wound: ['#400808', '#a01010', '#c04020', '#808040', '#40a040', '#20c060', '#10e080'],
};

// === Pre-baked LUTs ===

export const LUT = {
  cathedral: bakeLUT(PALETTE_STOPS.cathedral),
  thermal: bakeLUT(PALETTE_STOPS.thermal),
  polarity: bakeLUT(PALETTE_STOPS.polarity),
  conway: bakeLUT(PALETTE_STOPS.conway),
  mycelium: bakeLUT(PALETTE_STOPS.mycelium),
  network: bakeLUT(PALETTE_STOPS.network),
  mono: bakeLUT(PALETTE_STOPS.mono),
  storm: bakeLUT(PALETTE_STOPS.storm),
  riverCyan: bakeLUT(PALETTE_STOPS.riverCyan),
  riverMagenta: bakeLUT(PALETTE_STOPS.riverMagenta),
  riverGold: bakeLUT(PALETTE_STOPS.riverGold),
  clan0: bakeLUT(PALETTE_STOPS.clan0),
  clan1: bakeLUT(PALETTE_STOPS.clan1),
  clan2: bakeLUT(PALETTE_STOPS.clan2),
  clan3: bakeLUT(PALETTE_STOPS.clan3),
  clan4: bakeLUT(PALETTE_STOPS.clan4),
  clan5: bakeLUT(PALETTE_STOPS.clan5),
  fibonacci: bakeLUT(PALETTE_STOPS.fibonacci),
  phase: bakeLUT(PALETTE_STOPS.phase),
  wound: bakeLUT(PALETTE_STOPS.wound),
} as const;

export const CLAN_LUTS = [LUT.clan0, LUT.clan1, LUT.clan2, LUT.clan3, LUT.clan4, LUT.clan5];
export const RIVER_LUTS = [LUT.riverCyan, LUT.riverMagenta, LUT.riverGold];
